import { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react';

type FormStateInternal = Record<string, boolean>;

type FormState = {
  hasChangedValues: boolean;
};

type FormStateContextValue = {
  getFormState: (locationId: string) => FormState | undefined;
  setFormState: (locationId: string, state: FormState, source?: string) => void;
  clearFormState: (locationId: string) => void;
};

const FormStateContext = createContext<FormStateContextValue | undefined>(undefined);

type FormStateProviderProps = {
  children: ReactNode;
};

/**
 * Provider that manages form state across multiple locations.
 * Allows forms to register their state (e.g., hasChangedValues) and
 * other components to read that state.
 * Supports multiple sources of changes that are combined together.
 */
export const FormStateProvider = ({ children }: FormStateProviderProps) => {
  const [formStates, setFormStates] = useState<Record<string, FormStateInternal>>({});

  const getFormState = useCallback(
    (locationId: string): FormState | undefined => {
      const state = formStates[locationId];
      if (!state) return undefined;
      // Combine all sources - hasChangedValues is true if any source has changes
      const hasChangedValues = Object.values(state).some((v) => v === true);
      return { hasChangedValues };
    },
    [formStates]
  );

  const setFormState = useCallback((locationId: string, state: FormState, source = 'form') => {
    setFormStates((prev) => ({
      ...prev,
      [locationId]: {
        ...prev[locationId],
        [source]: state.hasChangedValues,
      },
    }));
  }, []);

  const clearFormState = useCallback((locationId: string) => {
    setFormStates((prev) => {
      const newStates = { ...prev };
      delete newStates[locationId];
      return newStates;
    });
  }, []);

  const contextValue = useMemo(
    () => ({ getFormState, setFormState, clearFormState }),
    [getFormState, setFormState, clearFormState]
  );

  return <FormStateContext.Provider value={contextValue}>{children}</FormStateContext.Provider>;
};

/**
 * Hook to access form state context.
 * Use this to read or update form state for specific locations.
 */
export const useFormStateContext = () => {
  const context = useContext(FormStateContext);
  if (!context) {
    throw new Error('useFormStateContext must be used within a FormStateProvider');
  }
  return context;
};
